import { skipToken } from '@reduxjs/toolkit/query'
import { isUndefined } from 'lodash-es'
import { useMemo } from 'react'
import { useFetchUserStatusQuery } from '@/api/user-api'
import { useFetchKolCollectionsQuery } from '@/api/workspace-api/kol-collections-api'
import { usePermissions } from '@/hooks/use-permissions'
import {
  BasicService,
  CollectionService,
  ServiceCategory,
} from '@/types/mock-api-types'

interface Limit {
  collectionCustomizedColumnAmount: number
  numberOfCollections: number
  numberOfKols: number
}

const UNLIMITED = Number.MAX_VALUE

type UseCollectionLimit = () => {
  limited: boolean
  limit: Limit
  isCollectionLimitReached: boolean
}

export const EXCEED_LIMITATION = 'exceed the resource limitation'

const useCollectionLimit: UseCollectionLimit = () => {
  const { data: userStatus } = useFetchUserStatusQuery()
  // TODO: remove skipToken.
  // 不 skipToken 的話，在 /collection/kol 登出會重複呼叫 agent-info
  // 此為治標寫法暫時避免多餘切且不必要的 api call
  const { data: collections } = useFetchKolCollectionsQuery(
    userStatus
      ? {
          page: 1,
        }
      : skipToken,
  )
  const { withInternalPermission } = usePermissions()

  const isCollectionService = (
    service?: BasicService,
  ): service is CollectionService =>
    service?.serviceCategory === ServiceCategory.Collection

  const foundCollectionService = (): CollectionService | undefined =>
    userStatus?.currentWorkspace.availableServices.find(isCollectionService)

  const collectionService = foundCollectionService()
  const limit: Limit = useMemo(() => {
    if (withInternalPermission) {
      return {
        collectionCustomizedColumnAmount: UNLIMITED,
        numberOfCollections: UNLIMITED,
        numberOfKols: UNLIMITED,
      }
    }

    if (collectionService !== undefined) {
      return {
        collectionCustomizedColumnAmount:
          collectionService.memo.collectionCustomizedColumnAmount,
        numberOfCollections: collectionService.memo.collectionLimit,
        numberOfKols: collectionService.memo.collectionKolLimit,
      }
    }

    return {
      collectionCustomizedColumnAmount: 0,
      numberOfCollections: 0,
      numberOfKols: 0,
    }
  }, [withInternalPermission, collectionService])

  const limited = limit.numberOfCollections !== UNLIMITED

  const isCollectionLimitReached =
    !limited || isUndefined(collections)
      ? false
      : collections.total >= limit.numberOfCollections

  return {
    limited,
    limit,
    isCollectionLimitReached,
  }
}

export default useCollectionLimit
